import { clearCheckoutHoldSession } from "./checkoutHoldSession";
import { readMenuOrderType, writeMenuOrderType } from "./menuOrderTypePreference";
import { clearSmartRouteSlot, readSmartRouteSlot } from "./smartRouteSlotStorage";
import {
  loadLastTravelTrip,
  loadTravelAddressHistory,
  travelAddressesMatch,
} from "./travelAddressHistory";

const LS_ACTIVE_TRIP = "smartRoute:activeTrip_v1";
/** Favorites Drive checkout: keep slot timing, skip multi-stop trip plan UI. */
const LS_FAVORITE_CHECKOUT = "smartRoute:driveFavoriteCheckout_v1";

function pointAddress(point) {
  return String(point?.formattedAddress || point?.address || "").trim();
}

function normalizePoint(point, { restaurantId } = {}) {
  if (!point) return null;
  const formattedAddress = pointAddress(point);
  const lat = Number(point.lat);
  const lng = Number(point.lng);
  if (!formattedAddress || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const rid = restaurantId || point.restaurantId;
  return {
    formattedAddress,
    lat,
    lng,
    placeId: point.placeId || null,
    ...(rid ? { restaurantId: String(rid) } : {}),
  };
}

/** Paid cart lines that can be stored as a Drive favorite. Skips offers and point rewards. */
export function savableDriveCartLines(items) {
  const lines = [];
  for (const item of items || []) {
    const menuItemId = String(item?.menuItemId?._id || item?.menuItemId || "").trim();
    if (!menuItemId || item?.offerId || item?.redeemWithPoints) continue;
    const fromLine = Array.isArray(item.selectedResolved) ? item.selectedResolved : [];
    const fromSelections = Array.isArray(item.selections?.selectedResolved)
      ? item.selections.selectedResolved
      : [];
    const selectedResolved = fromLine.length ? fromLine : fromSelections;
    const selections =
      item.selections && typeof item.selections === "object" ? { ...item.selections } : {};
    selections.selectedResolved = selectedResolved;
    lines.push({
      menuItemId,
      quantity: Math.max(1, Math.min(20, Number(item.quantity) || 1)),
      selections,
    });
  }
  return lines;
}

/** Address + coordinates, or null when the point cannot be stored on a favorite. */
export function toDrivePoint(point, { restaurantId } = {}) {
  return normalizePoint(point, { restaurantId });
}

/**
 * Departure shared by these Drive favorites.
 * Mixed means the dishes do not all use the same address.
 */
export function sharedDriveOrigin(items) {
  const points = (items || [])
    .map((item) => toDrivePoint(item?.driveTrip?.origin))
    .filter(Boolean);
  if (!points.length) return { origin: null, mixed: false };
  const origin = points[0];
  const mixed = points.some((point) => !travelAddressesMatch(point, origin));
  return { origin: mixed ? null : origin, mixed };
}

/** Persist the latest planned Drive trip for favorite save (survives menu navigation). */
export function saveActiveDriveTrip(trip) {
  try {
    const origin = normalizePoint(trip?.origin);
    const destination = normalizePoint(trip?.destination, {
      restaurantId: trip?.destination?.restaurantId,
    });
    if (!origin || !destination) return;
    sessionStorage.setItem(
      LS_ACTIVE_TRIP,
      JSON.stringify({
        origin,
        destination,
        destinationChosen: Boolean(trip?.destinationChosen),
        savedAt: Date.now(),
      })
    );
  } catch {
    /* ignore */
  }
}

export function loadActiveDriveTrip() {
  try {
    const raw = sessionStorage.getItem(LS_ACTIVE_TRIP);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.savedAt && Date.now() - parsed.savedAt > 2 * 60 * 60 * 1000) {
      sessionStorage.removeItem(LS_ACTIVE_TRIP);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearActiveDriveTrip() {
  try {
    sessionStorage.removeItem(LS_ACTIVE_TRIP);
  } catch {
    /* ignore */
  }
}

/** Mark this checkout as a Drive-favorites reorder (not Explore multi-stop). */
export function markDriveFavoriteCheckout() {
  try {
    sessionStorage.setItem(LS_FAVORITE_CHECKOUT, "1");
  } catch {
    /* ignore */
  }
}

export function consumeDriveFavoriteCheckout() {
  try {
    const on = sessionStorage.getItem(LS_FAVORITE_CHECKOUT) === "1";
    if (on) sessionStorage.removeItem(LS_FAVORITE_CHECKOUT);
    return on;
  } catch {
    return false;
  }
}

export function clearDriveFavoriteCheckout() {
  try {
    sessionStorage.removeItem(LS_FAVORITE_CHECKOUT);
  } catch {
    /* ignore */
  }
}

/**
 * Classic favorites open a takeaway/dine-in cart.
 * Drop a leftover Drive route / ASAP hold so the cart does not ask for a departure
 * and checkout shows normal ASAP / choose-slot controls again.
 * Also marks this restaurant like a feed cart so Global Drive checkout stays off
 * even after navigation loses `favoriteCartMode` (e.g. pickup checkout).
 * Callers should also run `releaseUnpaidGlobalDriveDraft()` (cannot import here —
 * driveMultiStopPlan already depends on this module).
 */
export function prepareClassicFavoriteCart(restaurantId) {
  const rid = String(restaurantId || "").trim();
  if (!rid) return;
  clearSmartRouteSlot(rid);
  clearCheckoutHoldSession(rid);
  clearDriveFavoriteCheckout();
  clearActiveDriveTrip();
  // Same key as feedCartOrder.markFeedCartChoice — avoid importing that module (cycle).
  try {
    sessionStorage.setItem(`feedCartOrder:${rid}`, "1");
  } catch {
    /* ignore */
  }
  if (readMenuOrderType(rid) === "DRIVE") writeMenuOrderType(rid, "TAKEAWAY");
}

/**
 * Build a driveTrip snapshot for saving a Drive favorite.
 * Prefers active session trip, then last planned trip; destination restaurantId from item.
 */
export function resolveDriveTripForFavorite(restaurantId) {
  const rid = String(restaurantId || "").trim();
  if (!rid) return null;

  const history = loadTravelAddressHistory();
  const fillAddressFromHistory = (point) => {
    if (!point) return null;
    const lat = Number(point.lat);
    const lng = Number(point.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    const existing = pointAddress(point);
    if (existing) return normalizePoint({ ...point, formattedAddress: existing });

    const match = history.find(
      (h) =>
        Math.abs(Number(h.lat) - lat) < 1e-4 && Math.abs(Number(h.lng) - lng) < 1e-4
    );
    const fromHistory = String(match?.formattedAddress || match?.address || "").trim();
    // Coords-only trips (older plans with empty formattedAddress) still need a label
    const fallback = fromHistory || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    return normalizePoint({
      ...point,
      formattedAddress: fallback,
      placeId: point.placeId || match?.placeId || null,
    });
  };

  const candidates = [loadActiveDriveTrip(), loadLastTravelTrip()];
  for (const trip of candidates) {
    const origin = fillAddressFromHistory(trip?.origin) || normalizePoint(trip?.origin);
    const destination =
      fillAddressFromHistory(trip?.destination) ||
      normalizePoint(trip?.destination, { restaurantId: rid });
    if (origin && destination) {
      return {
        origin,
        destination: {
          formattedAddress: destination.formattedAddress,
          lat: destination.lat,
          lng: destination.lng,
          placeId: destination.placeId || null,
        },
      };
    }
  }

  return null;
}

/** Near me Drive ends at the restaurant. A previous Global Drive To must not be reused. */
export function isNearMeExploreMode() {
  try {
    return String(localStorage.getItem("userExploreMode") || "") === "nearMe";
  } catch {
    return false;
  }
}

/**
 * True when the user reached item selection from Drive explore.
 * Uses session slot hint; if the slot expired, require Drive explore mode + last trip.
 * A restaurant Drive departure saved on the cart is not an Explore trip.
 */
export function isDriveFavoriteContext(restaurantId) {
  const rid = String(restaurantId || "").trim();
  if (!rid) return false;
  const slot = readSmartRouteSlot(rid);
  if (slot?.restaurantDrive) return false;
  if (slot?.source === "drive") return true;

  let exploreMode = "";
  try {
    exploreMode = String(localStorage.getItem("userExploreMode") || "");
  } catch {
    exploreMode = "";
  }
  if (exploreMode !== "drive" && exploreMode !== "travel") return false;

  return Boolean(resolveDriveTripForFavorite(rid));
}

/**
 * The cart already asks for this restaurant's Drive departure.
 * Item pages must not ask again while the customer adds food to meet the minimum.
 * Global Drive already collected From on Explore, so the item page does not ask again.
 */
export function cartOwnsRestaurantDriveDeparture(restaurantId, orderType) {
  const rid = String(restaurantId || "").trim();
  if (!rid) return false;
  const slot = readSmartRouteSlot(rid);
  if (slot?.restaurantDrive) return true;
  if (slot?.source === "drive" && !slot.restaurantDrive) return false;
  const type = orderType || readMenuOrderType(rid);
  return type === "DRIVE";
}

/**
 * @typedef {object} DriveFavoriteLaunch
 * @property {string} restaurantId
 * @property {object} origin
 * @property {object} destination
 * @property {Array<object>} favorites - full favorite docs for cart mapping
 * @property {string} [restaurantName]
 */

export function buildDriveFavoriteLaunch({ restaurantId, restaurantName, origin, destination, favorites }) {
  const rid = String(restaurantId || "").trim();
  if (!rid || !Array.isArray(favorites) || favorites.length === 0) return null;
  const o = normalizePoint(origin || favorites[0]?.driveTrip?.origin);
  const d = normalizePoint(destination || favorites[0]?.driveTrip?.destination);
  if (!o || !d) return null;

  return {
    restaurantId: rid,
    restaurantName: restaurantName || favorites[0]?.restaurantName || "",
    origin: o,
    // Keep To as the place endpoint; restaurantId lives on the launch, not on To
    destination: {
      formattedAddress: d.formattedAddress,
      lat: d.lat,
      lng: d.lng,
      placeId: d.placeId || null,
    },
    favorites,
  };
}
